// Library
import React, { useId, useMemo, useState } from 'react';
import { Text } from '@components';
import { WarningQld } from '@public/icons';
// DatePicker from 'react-date-picker' without styles;
import { DatePicker } from 'react-date-picker';

import { FieldWidth } from '..';

// CSS Style for this module
import styles from './dateField.module.scss';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

/**
 * Interface for DateFieldProps props
 */
interface DateFieldProps {
	/**
	 * Custom styles of the field
	 */
	customStyles?: string;
	/**
	 * Default value for edit mode
	 */
	defaultValue?: Date;
	/**
	 * Minimum restrictive date to pick
	 */
	minDate?: Date;
	/**
	 * Maximun restrictive date to pick
	 */
	maxDate?: Date;
	/**
	 * onChange function for keeping track field value
	 * debounce is built in
	 */
	handleChange?: (value: Date) => void;
	/**
	 * Helper text for additional guidance about how the input field will be used.
	 * It should only take up a single line
	 */
	hintText?: string;
	/**
	 * Msg to be displayed when the status is error
	 */
	errorMsg?: string;
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	disabled?: boolean;
	/**
	 * Width size of the input element.
	 * Accepts 'small', 'medium', 'large' or 'full' as fluid width.
	 * Or 'fixedWidth1' to 'fixedWidth6' for a standardized fixed width.
	 */
	size?: FieldWidth;
	/**
	 * Boolean for determining whether this field has to be autofocused
	 */
	autoFocus?: boolean;
	/** Element identifier selector for test */
	testId?: string;
}

export function DateField({
	customStyles,
	defaultValue,
	minDate,
	maxDate,
	hintText,
	errorMsg,
	disabled,
	handleChange = () => {
		return;
	},
	size = 'full',
	autoFocus,
	testId,
}: DateFieldProps) {
	const internalId = useId();
	const [hintId] = useState(`${internalId}-hint`);
	const [errorId] = useState(`${internalId}-error`);
	const [date, setDate] = useState(defaultValue);
	const [isFocused, setIsFocused] = useState(false);
	const hasError = useMemo(() => Boolean(errorMsg), [errorMsg]);

	const onChangeValue = (value: Date) => {
		setDate(value);
		handleChange(value);
	};

	return (
		<div className={[styles.container, customStyles].join(' ')}>
			<div className={styles.textContainer}>
				{hintText && (
					<div>
						<Text variant="muted" id={hintId}>
							{hintText}
						</Text>
					</div>
				)}
				{hasError && (
					<div className={styles.errorContainer}>
						<Text variant="error" id={errorId} role="alert" className={styles.errorContent}>
							<WarningQld customStyles={styles.errorIcon} /> {errorMsg}
						</Text>
					</div>
				)}
			</div>
			<DatePicker
				calendarAriaLabel="Toggle calendar"
				onChange={(value: Value) => onChangeValue(value as Date)}
				value={date}
				dayPlaceholder="dd"
				monthPlaceholder="mm"
				yearPlaceholder="yyyy"
				clearIcon={null}
				format="dd/MM/y"
				minDate={minDate}
				maxDate={maxDate}
				className={[
					styles[`field-width--${size}`],
					styles.datepicker,
					isFocused && styles.focused,
					disabled && styles.disabled,
					hasError && styles.error,
				].join(' ')}
				onFocus={() => setIsFocused(true)}
				onBlur={() => setIsFocused(false)}
				onCalendarOpen={() => setIsFocused(true)}
				onCalendarClose={() => setIsFocused(false)}
				openCalendarOnFocus={true}
				shouldOpenCalendar={() => true}
				disabled={disabled}
				autoFocus={autoFocus}
				data-testid={testId}
			/>
		</div>
	);
}
